import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

type AddonOpt = { id: string; label: string; price_delta: number };
type AvailAddon = { id: string; name: string; price: number; max_quantity: number | null; options: AddonOpt[] };
type Pick = { addon_id: string; option_id: string | null; option_label: string | null; unit_price: number; quantity: number };

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function EditBookingDialog({
  open, onOpenChange, booking, addons: existingAddons, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  booking: any;
  addons: any[];
  onSaved: () => void;
}) {
  const [studios, setStudios] = useState<{ id: string; name: string; hourly_rate: number }[]>([]);
  const [draft, setDraft] = useState({
    studio_id: "", start_at: "", end_at: "",
    status: "pending", payment_status: "unpaid",
    base_override: "", amount_paid: "",
    notes: "", internal_notes: "",
  });
  const [available, setAvailable] = useState<AvailAddon[]>([]);
  const [picked, setPicked] = useState<Record<string, Pick>>({});
  const [optionPick, setOptionPick] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Seed draft + picks whenever the dialog opens with a booking.
  useEffect(() => {
    if (!open || !booking) return;
    setDraft({
      studio_id: booking.studio_id ?? "",
      start_at: booking.start_at ? toLocalInput(booking.start_at) : "",
      end_at: booking.end_at ? toLocalInput(booking.end_at) : "",
      status: booking.status ?? "pending",
      payment_status: booking.payment_status ?? "unpaid",
      base_override: "",
      amount_paid: booking.amount_paid != null ? String(booking.amount_paid) : "0",
      notes: booking.notes ?? "",
      internal_notes: booking.internal_notes ?? "",
    });
    const seeded: Record<string, Pick> = {};
    (existingAddons ?? []).forEach((a: any) => {
      const key = `${a.addon_id}::${a.option_id ?? ""}`;
      seeded[key] = {
        addon_id: a.addon_id, option_id: a.option_id ?? null,
        option_label: a.option_label ?? null,
        unit_price: Number(a.unit_price), quantity: Number(a.quantity),
      };
    });
    setPicked(seeded);
    setOptionPick({});
  }, [open, booking, existingAddons]);

  // Load studios + addons.
  useEffect(() => {
    if (!open) return;
    supabase.from("studios").select("id,name,hourly_rate").eq("active", true)
      .order("display_order").then(({ data }) => setStudios(data ?? []));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: globals } = await supabase.from("addons")
        .select("id,name,price,max_quantity")
        .eq("active", true).is("product_id", null).is("studio_id", null).order("display_order");
      let studioScoped: any[] = [];
      if (draft.studio_id) {
        const { data: legacy } = await supabase.from("addons")
          .select("id,name,price,max_quantity").eq("active", true).eq("studio_id", draft.studio_id);
        const { data: links } = await supabase.from("addon_studios")
          .select("addon_id").eq("studio_id", draft.studio_id);
        const linkIds = (links ?? []).map((x: any) => x.addon_id);
        let linked: any[] = [];
        if (linkIds.length) {
          const { data } = await supabase.from("addons")
            .select("id,name,price,max_quantity").in("id", linkIds).eq("active", true);
          linked = data ?? [];
        }
        studioScoped = [...(legacy ?? []), ...linked];
      }
      const seen = new Set<string>();
      const merged = [...(globals ?? []), ...studioScoped].filter(a => {
        if (seen.has(a.id)) return false; seen.add(a.id); return true;
      });
      const ids = merged.map(a => a.id);
      const byAddon = new Map<string, AddonOpt[]>();
      if (ids.length) {
        const { data: opts } = await supabase.from("addon_options")
          .select("id,addon_id,label,price_delta,display_order").in("addon_id", ids).order("display_order");
        (opts ?? []).forEach((o: any) => {
          const arr = byAddon.get(o.addon_id) ?? [];
          arr.push({ id: o.id, label: o.label, price_delta: Number(o.price_delta) || 0 });
          byAddon.set(o.addon_id, arr);
        });
      }
      setAvailable(merged.map(a => ({ ...a, options: byAddon.get(a.id) ?? [] })));
    })();
  }, [open, draft.studio_id]);

  const hours = useMemo(() => {
    if (!draft.start_at || !draft.end_at) return 0;
    const ms = new Date(draft.end_at).getTime() - new Date(draft.start_at).getTime();
    return ms > 0 ? ms / 3600_000 : 0;
  }, [draft.start_at, draft.end_at]);

  const studio = studios.find(s => s.id === draft.studio_id);
  const baseAuto = studio ? Number((studio.hourly_rate * hours).toFixed(2)) : 0;
  const baseOverride = draft.base_override !== "" ? Number(draft.base_override) : NaN;
  const base = !Number.isNaN(baseOverride) ? baseOverride : baseAuto;
  const addonsTotal = Object.values(picked).reduce((s, l) => s + l.unit_price * l.quantity, 0);
  const total = Number((base + addonsTotal).toFixed(2));

  const save = async () => {
    if (!booking) return;
    if (!draft.studio_id) return toast.error("Studio is required");
    if (!draft.start_at || !draft.end_at) return toast.error("Start and end are required");
    if (new Date(draft.end_at) <= new Date(draft.start_at)) return toast.error("End must be after start");

    setSaving(true);
    try {
      const amount_paid = draft.amount_paid !== "" ? Number(draft.amount_paid) : 0;
      const { error: upErr } = await supabase.from("bookings").update({
        studio_id: draft.studio_id,
        start_at: new Date(draft.start_at).toISOString(),
        end_at: new Date(draft.end_at).toISOString(),
        status: draft.status as any,
        payment_status: draft.payment_status as any,
        subtotal: total,
        total,
        amount_paid,
        notes: draft.notes || null,
        internal_notes: draft.internal_notes || null,
      }).eq("id", booking.id);
      if (upErr) throw upErr;

      // Replace add-ons: delete + reinsert. Triggers log stock audit deltas.
      const { error: delErr } = await supabase.from("booking_addons").delete().eq("booking_id", booking.id);
      if (delErr) throw delErr;
      const rows = Object.values(picked).filter(l => l.quantity > 0).map(l => ({
        booking_id: booking.id,
        addon_id: l.addon_id,
        option_id: l.option_id,
        option_label: l.option_label,
        quantity: l.quantity,
        unit_price: l.unit_price,
      }));
      if (rows.length) {
        const { error: insErr } = await supabase.from("booking_addons").insert(rows);
        if (insErr) throw insErr;
      }

      await supabase.from("admin_audit_log").insert({
        action: "booking_edit",
        entity_type: "booking",
        entity_id: booking.id,
        after: { studio_id: draft.studio_id, total, amount_paid, status: draft.status, payment_status: draft.payment_status, addon_count: rows.length },
      });

      toast.success("Booking updated");
      onOpenChange(false);
      onSaved();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update booking");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit booking</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <Label>Studio</Label>
              <Select value={draft.studio_id} onValueChange={(v) => setDraft({ ...draft, studio_id: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {studios.map(s => <SelectItem key={s.id} value={s.id}>{s.name} — ${s.hourly_rate}/hr</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start</Label>
              <Input type="datetime-local" value={draft.start_at} onChange={e => setDraft({ ...draft, start_at: e.target.value })}/>
            </div>
            <div>
              <Label>End</Label>
              <Input type="datetime-local" value={draft.end_at} onChange={e => setDraft({ ...draft, end_at: e.target.value })}/>
            </div>
            <div>
              <Label>Hours</Label>
              <Input value={hours.toFixed(2)} readOnly className="bg-muted"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Status</Label>
              <Select value={draft.status} onValueChange={v => setDraft({ ...draft, status: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["pending","confirmed","completed","cancelled","refunded"].map(s =>
                    <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment status</Label>
              <Select value={draft.payment_status} onValueChange={v => setDraft({ ...draft, payment_status: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["unpaid","partial","paid","refunded"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Add-ons</Label>
            {available.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No add-ons available for this studio.</p>
            ) : (
              <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                {available.map(a => {
                  const hasOpts = a.options.length > 0;
                  const max = a.max_quantity ?? 99;
                  const selOptId = optionPick[a.id] ?? (hasOpts ? a.options[0].id : "");
                  const selOpt = hasOpts ? a.options.find(o => o.id === selOptId) ?? a.options[0] : null;
                  const key = `${a.id}::${selOpt?.id ?? ""}`;
                  const line = picked[key];
                  const q = line?.quantity ?? 0;
                  const unit = Number(a.price) + (selOpt?.price_delta ?? 0);
                  const setQty = (next: number) => {
                    const n = Math.max(0, Math.min(max, next));
                    setPicked(p => {
                      const c = { ...p };
                      if (n === 0) { delete c[key]; return c; }
                      c[key] = { addon_id: a.id, option_id: selOpt?.id ?? null, option_label: selOpt?.label ?? null, unit_price: unit, quantity: n };
                      return c;
                    });
                  };
                  return (
                    <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm">{a.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">${unit.toFixed(2)}</div>
                        {hasOpts && (
                          <Select value={selOpt!.id} onValueChange={v => setOptionPick(o => ({ ...o, [a.id]: v }))}>
                            <SelectTrigger className="h-7 text-xs mt-1 max-w-[260px]"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              {a.options.map(o => <SelectItem key={o.id} value={o.id}>{o.label}{o.price_delta ? ` (+$${o.price_delta.toFixed(2)})` : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setQty(q - 1)}>−</Button>
                        <span className="w-6 text-center font-mono text-sm">{q}</span>
                        <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setQty(q + 1)}>+</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Base override ($)</Label>
              <Input type="number" step="0.01" placeholder={`auto ${baseAuto.toFixed(2)}`} value={draft.base_override}
                onChange={e => setDraft({ ...draft, base_override: e.target.value })}/>
            </div>
            <div>
              <Label>Amount paid ($)</Label>
              <Input type="number" step="0.01" value={draft.amount_paid}
                onChange={e => setDraft({ ...draft, amount_paid: e.target.value })}/>
            </div>
            <div className="self-end">
              <div className="border border-border p-2 text-xs font-mono">
                <div className="flex justify-between"><span>Base</span><span>${base.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Add-ons</span><span>${addonsTotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-primary"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>

          <div>
            <Label>Customer notes</Label>
            <Textarea rows={2} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })}/>
          </div>
          <div>
            <Label>Internal notes</Label>
            <Textarea rows={2} value={draft.internal_notes} onChange={e => setDraft({ ...draft, internal_notes: e.target.value })}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}